import React from 'react';
import {
  CollapseLabel,
  CollapseLabelDivider,
  InfoIcon,
  TextButton,
} from '@wix/wix-base-ui';
import { ApplyTo } from '@wix/wix-ui-icons-common/classic-editor';
import {
  EffectConfig,
  EffectRangeAll,
  getDefaults,
  isLegacy,
  OneOfEffects,
  OneOfEntranceEffects,
  OneOfEntranceOngoingEffects,
  OneOfScrollEffects,
} from '@wix/effects-kit';

import { AnimationList } from './AnimationsList';
import { AnimationCustomize } from './AnimationCustomize';
import { TranslationContext } from '../context/TranslationContext';
import { AnimationSectionSuffix } from '../components/AnimationSectionSuffix';
import { AnimationCustomizeButton } from '../components/AnimationCustomizeButton';
import { cleanupPanelEffect } from '../assets/cleanupPanelEffect';

import EntranceThumbs from '../assets/thumbs/entrance';
import OngoingThumbs from '../assets/thumbs/ongoing';
import ScrollThumbs from '../assets/thumbs/scroll';
import translationKeys from '../assets/translations.json';
import sortOrder from '../assets/animationListSortOrder.json';
import styles from '../assets/styles.scss';

import type {
  EffectParams,
  ScrollEffectParams,
  ScrollEffectConfig,
  Trigger,
} from '@wix/effects-kit';

import type {
  MotionPanelAPI,
  MotionPanelProps,
  AnimationCustomizeProps,
  AnimationScrollCustomizeProps,
  AnimationsListProps,
  TranslateFunc,
  PanelEffects,
} from '../types';
import { AnimationScrollCustomize } from './AnimationScrollCustomize';

const NO_SELECTION = {
  entrance: { name: '' },
  ongoing: { name: '' },
  scroll: { name: '' },
};

const isAnimation = (name: string) => !['', 'None'].includes(name);

export const MotionPanel: React.FC<MotionPanelProps> = React.forwardRef<
  MotionPanelAPI,
  MotionPanelProps
>(
  (
    {
      translate: externalTranslate = (key) => key,
      selected: rawSelected = NO_SELECTION,
      onChange,
      onPreview,
      onPreviewStop,
      onApplyFromDesktop,
      onLegacyMessageClick,
      isScrollPreview,
      isMobileEditor,
      isApplyButtonVisible,
      isApplyButtonDisabled,
      allowTriggerMixing,
      entranceAnimations = [],
      legacyEntranceAnimations = [],
      ongoingAnimations = [],
      scrollAnimations = [],
      reportAnimationChange,
      reportCustomizeClick,
      reportParamChange,
      alwaysShowCollapseInList,
    },
    ref,
  ) => {
    const translate = React.useCallback<TranslateFunc>(
      (key, values) =>
        externalTranslate(
          translationKeys[key as keyof typeof translationKeys],
          values,
        ),
      [externalTranslate],
    );

    // Memoize animations presets, they won't change during the lifecycle of the panel
    const animations = React.useMemo(
      () =>
        [
          {
            trigger: 'entrance',
            presets: entranceAnimations.concat(legacyEntranceAnimations),
          },
          { trigger: 'ongoing', presets: ongoingAnimations },
          { trigger: 'scroll', presets: scrollAnimations },
        ] as const,
      [
        entranceAnimations,
        legacyEntranceAnimations,
        ongoingAnimations,
        scrollAnimations,
      ],
    );

    const getPreset = React.useCallback(
      (trigger: Trigger, name: string) => {
        const presets = animations.find(
          (animation) => animation.trigger === trigger,
        )?.presets;
        return (presets as EffectConfig<OneOfEffects>[])?.find(
          (animation: EffectConfig<OneOfEffects>) => animation.name === name,
        );
      },
      [animations],
    );

    const initSelected = () => ({
      entrance: cleanupPanelEffect(
        rawSelected.entrance,
        getPreset('entrance', rawSelected.entrance.name) || null,
      ),
      ongoing: cleanupPanelEffect(
        rawSelected.ongoing,
        getPreset('ongoing', rawSelected.ongoing.name) || null,
      ),
      scroll: cleanupPanelEffect(
        rawSelected.scroll,
        getPreset('scroll', rawSelected.scroll.name) || null,
      ),
    });

    // use cleanupPanelEffect to remove noise from data
    // cleanupPanelEffect accepts a list of animations and an animation schema
    const selected = React.useMemo(initSelected, [getPreset, rawSelected]);

    const initialSection =
      Object.keys(selected).filter((trigger) =>
        isAnimation(selected[trigger as Trigger].name),
      )[0] || 'entrance';

    // Current Section (naming same as trigger)
    const [section, setSection] = React.useState<Trigger | ''>(
      initialSection as Trigger,
    );
    // Current customize mode of a section (naming same as trigger)
    const [customizeMode, setCustomizeMode] = React.useState<Trigger>();

    // If the data had a legacy entrance animation, preserve it's value through the panel lifecycle
    const legacyPreset = React.useRef<
      EffectConfig<OneOfEntranceEffects> | undefined
    >();

    // Preserve params between preset changes
    const [cachedParams, setCachedParams] = React.useState<
      Record<string, EffectParams | ScrollEffectParams | undefined>
    >(
      Object.fromEntries(
        Object.values(selected)
          .filter(({ name }) => isAnimation(name))
          .map(({ name, params }) => [name, params]),
      ),
    );
    const getCachedParams = (name: string) => cachedParams[name];
    const cacheParamsByName = (name: string, params: EffectParams) =>
      setCachedParams((prev) => ({ ...prev, [name]: params }));

    // API for showing the back button in Panel Frame on customize mode
    React.useImperativeHandle(
      ref,
      () => ({
        exitCustomizeMode: () => setCustomizeMode(undefined),
        isCustomizeMode: customizeMode !== undefined,
        getCurrentAnimationNameKey: () =>
          translationKeys[
            `name_${
              customizeMode
                ? selected[customizeMode].name.toLowerCase()
                : 'none'
            }` as keyof typeof translationKeys
          ],
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [customizeMode],
    );

    // Scroll animation presets has another level of nesting
    const onScrollAnimationChange = (
      name: string,
      params?: ScrollEffectParams,
      _preset?: ScrollEffectConfig<OneOfScrollEffects>,
    ) => {
      if (name !== 'None' && _preset) {
        const { range, schema } = _preset;
        const schemaRange = params?.range ?? range.default;
        params =
          params ??
          (getCachedParams(name) as ScrollEffectParams) ??
          getDefaults(
            {
              ...schema[schemaRange]!,
              range,
            } as ScrollEffectConfig<OneOfScrollEffects>['schema'],
            isMobileEditor ? 'mobileDefault' : 'default',
          );
      }
      onAnimationChange({
        trigger: 'scroll',
        name,
        params,
        removeTriggers: ['entrance', 'ongoing'],
      });
    };

    // - Update selected animation,
    //   if preset is passed use its params as defaults if no previous values are saved
    // Side effects:
    // - Remove other triggers if needed
    // - Update previous params
    const onAnimationChange = (
      {
        trigger,
        name,
        params,
        removeTriggers = [],
      }: {
        trigger: Trigger;
        name: string;
        params?: EffectParams | ScrollEffectParams;
        removeTriggers?: Trigger[];
      },
      _preset?: EffectConfig<OneOfEntranceOngoingEffects>,
    ) => {
      const removed: Partial<Record<Trigger, { name: 'None' }>> = {};

      if (name !== 'None') {
        if (_preset) {
          params =
            params ?? getCachedParams(name) ?? getDefaults(_preset.schema!);
        }

        cacheParamsByName(name, params as EffectParams);

        if (!allowTriggerMixing) {
          removeTriggers.forEach(
            (_trigger) => (removed[_trigger] = { name: 'None' }),
          );
        }
      }

      // For "multiselect" case - convert empty triggers to 'None'
      const selectedWithNoEmpties = Object.fromEntries(
        Object.entries(selected).map(([key, value]) => [
          key,
          { ...value, name: value.name || 'None' },
        ]),
      ) as PanelEffects;

      onChange({
        ...selectedWithNoEmpties,
        ...removed,
        [trigger]: { name, params },
      });
    };

    // Set panel to customize mode and report bi
    const enterCustomizeMode = (trigger: Trigger) => {
      setCustomizeMode(trigger);
      reportCustomizeClick?.({
        trigger_type: trigger,
        behavior_name: selected[trigger].name,
      });
    };

    const getCollapseLabelProps = (trigger: Trigger) => ({
      label: translate(`section_${trigger}`),
      shouldTranslate: false,
      customToggle: () => setSection(section !== trigger ? trigger : ''),
      isOpen: section === trigger,
      alwaysShowSuffix: isAnimation(selected[trigger].name),
      suffix: (
        <AnimationSectionSuffix
          tooltipText={`section_${trigger}_hover`}
          animationName={selected[trigger].name}
        />
      ),
      divider: 'none' as CollapseLabelDivider,
    });

    // Props for AnimationList component by trigger - scroll is a bit different
    const getAnimationListProps = (trigger: Trigger): AnimationsListProps => {
      const value = selected[trigger];
      const preset = getPreset(trigger, value.name);
      // If selected preset is legacy entrance, presist it's value
      if (preset && isLegacy(preset)) {
        legacyPreset.current = preset;
      }

      const onClickPreview = onPreview
        ? () => onPreview(trigger, value.name, value.params)
        : undefined;

      const onHoverPreview = onPreview
        ? (name: string) =>
            onPreview(
              trigger,
              name,
              getDefaults(getPreset(trigger, name)!.schema, 'previewDefault'),
            )
        : undefined;

      const shouldShowTriggerRemoveMessage =
        !allowTriggerMixing &&
        // Current animation is None
        value.name === 'None' &&
        // cureent section is scroll and there is another animation selected
        ((trigger === 'scroll' &&
          Object.entries(selected).some(
            ([_trigger, { name }]) => isAnimation(name) && trigger !== _trigger,
          )) ||
          // or current section is not scroll and scroll animation is selected
          (trigger !== 'scroll' && isAnimation(selected.scroll.name)));

      const shouldShowLegacyEntranceMessage =
        legacyPreset.current && trigger === 'entrance';

      let message: AnimationsListProps['message'] | undefined;

      // If we have a Trgger remove message, show it
      if (shouldShowTriggerRemoveMessage) {
        message = {
          text: translate(`section_${trigger}_remove_others_message`),
        };
      }
      // Else if we are in legacy mode, show legacy message
      else if (shouldShowLegacyEntranceMessage) {
        message = {
          text: translate('section_entrance_legacy_message'),
          link: onLegacyMessageClick && {
            label: translate('section_entrance_legacy_message_link'),
            onClick: onLegacyMessageClick,
          },
        };
      }

      const propsByTrigger: Record<
        Trigger,
        Omit<AnimationsListProps, 'value' | 'trigger'>
      > = {
        entrance: {
          animations: entranceAnimations.concat(legacyPreset.current || []),
          thumbs: EntranceThumbs,
          onClickPreview,
          onHoverPreview,
          onPreviewStop,
          onChange: (name: string) => {
            onAnimationChange(
              {
                trigger: 'entrance',
                name,
                removeTriggers: ['scroll'],
              },
              getPreset('entrance', name),
            );
            reportAnimationChange?.({
              trigger_type: 'entrance',
              behavior_name: name,
            });
          },
        },
        ongoing: {
          animations: ongoingAnimations,
          thumbs: OngoingThumbs,
          onClickPreview,
          onHoverPreview,
          onPreviewStop,
          onChange: (name: string) => {
            onAnimationChange(
              {
                trigger: 'ongoing',
                name,
                removeTriggers: ['scroll'],
              },
              getPreset('ongoing', name),
            );
            reportAnimationChange?.({
              trigger_type: 'ongoing',
              behavior_name: name,
            });
          },
        },
        scroll: {
          animations: scrollAnimations,
          thumbs: ScrollThumbs,
          onChange: (name: string) => {
            onScrollAnimationChange(
              name,
              undefined,
              getPreset(
                'scroll',
                name,
              ) as ScrollEffectConfig<OneOfScrollEffects>,
            );
            reportAnimationChange?.({
              trigger_type: 'scroll',
              behavior_name: name,
            });
          },
        },
      };

      return {
        sortOrder: sortOrder[trigger],
        value,
        trigger,
        message,
        ...propsByTrigger[trigger],
      };
    };

    const getAnimationCustomizeProps = (trigger: Trigger) => {
      const preset = getPreset(trigger, selected[trigger].name);
      const presetSchema = preset!.schema;
      const presetRangeSchema = (
        preset! as ScrollEffectConfig<ScrollEffectParams>
      ).range;
      const values = selected[trigger].params!;
      const props = {
        trigger,
        effectName: selected[trigger].name,
        schema: presetSchema,
        values,
        onChange: (
          name: string,
          key: keyof (EffectParams | ScrollEffectParams),
          value: any,
        ) => {
          if ((key as keyof ScrollEffectParams) === 'range') {
            // everytime we change scroll range, we need to update start and end
            const { start, end } = getDefaults(
              (
                presetSchema as ScrollEffectConfig<OneOfScrollEffects>['schema']
              )[value as EffectRangeAll]!,
            ) as ScrollEffectParams;

            onAnimationChange({
              trigger,
              name,
              params: { ...values, range: value, start, end },
            });
          } else {
            onAnimationChange({
              trigger,
              name,
              params: { ...values, [key]: value },
            });
          }
          reportParamChange?.({
            trigger_type: trigger,
            behavior_name: name,
            field_name: key,
            field_value: value,
          });
        },
        onPreview,
        onPreviewStop,
      };
      // Scroll animation has another level of nesting and different preview mechanism
      if (presetRangeSchema) {
        return {
          ...props,
          rangeSchema: presetRangeSchema,
          isScrollPreview,
        } as AnimationScrollCustomizeProps;
      }
      return props as AnimationCustomizeProps;
    };

    const showApplyToDesktop =
      customizeMode === undefined && isMobileEditor && isApplyButtonVisible;

    const animationSections = animations.filter(
      ({ presets }) => presets.length,
    );
    const renderAnimationSection = (trigger: Trigger) => (
      <div className={styles.sectionInner}>
        <AnimationList {...getAnimationListProps(trigger)} />
        <AnimationCustomizeButton
          trigger={trigger}
          onClick={enterCustomizeMode}
          isDisabled={
            !isAnimation(selected[trigger].name) ||
            legacyPreset.current?.name === selected[trigger].name
          }
        />
      </div>
    );

    const sectionHeaderCount =
      animationSections.length === 1 && !alwaysShowCollapseInList
        ? 0
        : animationSections.length;

    return (
      <TranslationContext.Provider
        value={{
          translate,
          prefix:
            section === 'scroll' && customizeMode
              ? `scroll_${(selected.scroll.params as ScrollEffectParams).range}`
              : section,
        }}
      >
        <div
          className={styles.motionPanel}
          style={
            {
              '--section-headers-count': sectionHeaderCount,
              '--section-padding-top': sectionHeaderCount ? '0px' : '12px',
              '--apply-button-height': showApplyToDesktop ? '44px' : '0px',
            } as React.CSSProperties
          }
        >
          {customizeMode === undefined ? (
            animationSections.map(({ trigger }) =>
              animationSections.length === 1 && !alwaysShowCollapseInList ? (
                <>{renderAnimationSection(trigger as Trigger)}</>
              ) : (
                <CollapseLabel
                  key={trigger}
                  {...getCollapseLabelProps(trigger as Trigger)}
                >
                  {renderAnimationSection(trigger as Trigger)}
                </CollapseLabel>
              ),
            )
          ) : customizeMode === ('scroll' as Trigger) ? (
            <AnimationScrollCustomize
              {...(getAnimationCustomizeProps(
                customizeMode,
              ) as AnimationScrollCustomizeProps)}
            />
          ) : (
            <AnimationCustomize
              {...(getAnimationCustomizeProps(
                customizeMode,
              ) as AnimationCustomizeProps)}
            />
          )}
        </div>
        {showApplyToDesktop && (
          <div className={styles.applyFromDesktopButton}>
            <TextButton
              size="small"
              onClick={onApplyFromDesktop}
              prefixIcon={<ApplyTo />}
              disabled={isApplyButtonDisabled}
              shouldTranslate={false}
            >
              {translate('apply_from_desktop_button')}
            </TextButton>
            <InfoIcon
              className={styles.applyFromDesktopInfoIcon}
              shouldTranslate={false}
              text={translate('apply_from_desktop_tooltip')}
            />
          </div>
        )}
      </TranslationContext.Provider>
    );
  },
);
